/**
 * Shared Utility Functions
 */

// === Auth/Token ===
export const getToken = () => localStorage.getItem("cpi_token");
export const setToken = (t) => t ? localStorage.setItem("cpi_token", t) : localStorage.removeItem("cpi_token");

// Kurzlebiges Token (60s) fuer EventSource/SSE holen. Der Aufruf laeuft ueber den Bearer-Header,
// das Sitzungstoken wandert also NICHT in die URL - nur dieses Sekunden-Token, das schnell verfaellt.
export const getSseToken = async () => {
  const t = getToken();
  if (!t) return null;
  try {
    const r = await fetch("/api/auth/sse-token", { method: "POST", headers: { Authorization: `Bearer ${t}` } });
    if (!r.ok) return null;
    return (await r.json()).token || null;
  } catch { return null; }
};

// === API Client ===
// opts.timeout: Millisekunden bis zum AbortController.abort(). Default 30000.
//   Setze opts.timeout = 0 (oder einen großen Wert wie 600000) für lang laufende
//   Endpunkte wie /api/cicd/transport/release, deren Backend spuerbar arbeitet
//   (Git-Tag anlegen, PD-Blobs lesen, Workflow-Dispatch).
export const api = async (url, opts = {}) => {
  const t = getToken();
  const method = (opts.method || "GET").toUpperCase();
  const maxRetries = method === "GET" ? 3 : 0;
  const timeoutMs = typeof opts.timeout === "number" ? opts.timeout : 30000;
  const fetchOpts = { ...opts };
  delete fetchOpts.timeout;
  // User Signal abfangen damit fetch sauber abgebrochen werden kann
  const userSignal = fetchOpts.signal;
  delete fetchOpts.signal;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
    if (userSignal) {
      if (userSignal.aborted) { if (timer) clearTimeout(timer); throw new DOMException("Aborted", "AbortError"); }
      userSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    try {
      const r = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          // Servermeldungen folgen der Oberflaeche. Der Header haengt hier und
          // nicht an den Aufrufstellen: es sind ueber tausend, und eine
          // vergessene liefert eine deutsche Meldung in einer englischen Maske.
          // Der Server faellt bei unbekannten Werten auf Deutsch zurueck.
          "Accept-Language": (typeof window !== "undefined" && window.__iioLang === "en") ? "en" : "de",
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        signal: controller.signal,
        ...fetchOpts,
      });
      if (timer) clearTimeout(timer);
      if (r.status === 401) { setToken(null); window.location.reload(); return; }
      if (method === "GET" && [502, 503, 504].includes(r.status) && attempt < maxRetries) {
        await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt)));
        continue;
      }
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        const base = e.error || `HTTP ${r.status}`;
        const msg = e.hint ? `${base} - ${e.hint}` : base;
        const err = new Error(msg);
        err.status = r.status;
        err.details = e.details;
        err.hint = e.hint;
        // Fachliche Begruendung des Backends durchreichen (z.B. Tenant-Ablehnung),
        // damit die UI keinen technischen Rohtext anzeigen muss.
        err.reason = e.reason;
        // Maschinenlesbare Kennung des Backends. Ein Textvergleich auf die Meldung traegt nicht:
        // sie kommt je nach Accept-Language auf Deutsch oder Englisch zurueck.
        err.code = e.code;
        // Restliche Antwortfelder anhaengen, damit ein Endpunkt Zusatzangaben mitgeben kann
        // (z.B. welche Laeufe eine Aktion gerade blockieren), ohne diesen Helfer zu aendern.
        err.body = e;
        err.unavailable = e.unavailable;
        throw err;
      }
      return r.json();
    } catch (err) {
      if (timer) clearTimeout(timer);
      if (err.name === "AbortError" && attempt < maxRetries) {
        await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt)));
        continue;
      }
      throw err;
    }
  }
};

// === CSS Variable Accessor ===
export const v = (name) => `var(--${name})`;

// === OData Date Parsing ===
export const parseODataDate = (d) => {
  if (typeof d !== 'string') return 0;
  const m = d.match(/\/Date\((\d+)\)\//);
  return m ? parseInt(m[1]) : 0;
};

// === Format Date ===
// Das Gebietsschema folgt der gewaehlten Sprache. Fest auf Deutsch gestellt
// erschiene in der englischen Oberflaeche "12.08.2026, 11:25:03", was dort als
// Datum falsch gelesen wird (Tag und Monat sind vertauscht).
// Gelesen wird der Wert, den src/i18n beim Umschalten am Fenster hinterlegt.
// Diese Datei kennt i18next bewusst nicht: sie wird auch ausserhalb von React
// benutzt, und ein Hook waere dort nicht verfuegbar.
// EXPORTIERT, damit niemand die Ableitung ein zwanzigstes Mal abschreibt: sie
// steht heute in 19 Dateien, in drei verschiedenen Bauformen. Zwei davon liegen
// auf Modulebene - und wer dort statt einer FUNKTION einen WERT schreibt, friert
// die Sprache beim Import ein. Der Fehler sieht dann aus wie die 16 richtigen
// Faelle und wechselt nie mehr die Sprache.
// Als Funktion aufrufen, nicht als Wert ablegen: gebietsschema(), nicht = gebietsschema.
export const gebietsschema = () =>
  (typeof window !== "undefined" && window.__iioLang === "en") ? "en-GB" : "de-DE";

export const formatDate = (d) => {
  const t = parseODataDate(d);
  return t
    ? new Intl.DateTimeFormat(gebietsschema(), {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).format(new Date(t))
    : "-";
};

// === Format Duration ===
export const formatDuration = (start, end) => {
  const ms = parseODataDate(end) - parseODataDate(start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

// === Tenant URL → Suite URL ===
export const toSuiteUrl = (tenantUrl, suiteUrl) => {
  if (suiteUrl) return suiteUrl.replace(/\/+$/, "");
  if (!tenantUrl) return "";
  return tenantUrl.replace(/\.it-[a-z0-9-]+\.cfapps/, ".integrationsuite.cfapps");
};

// === Monitoring Deep Link ===
export const monitoringLink = (baseUrl, guid) => {
  const g = typeof guid === "string" ? guid : guid?.MessageGuid || "";
  return `${baseUrl}/shell/monitoring/Messages/${encodeURIComponent(JSON.stringify({ identifier: g }))}`;
};

// CI/CD-Zugang: globale Admins ODER Space-Admins (Eigentuemer bzw. per Freigabe mit Rolle
// admin). Das Backend prueft dieselbe Regel je Space (requireCicdAdmin) - hier haengt nur,
// WAS die Oberflaeche anbietet. spaces ist die /api/spaces-Antwort (role je Space).
export const hatCicdZugang = (user, spaces) =>
  !!user && (user.role === "admin" || (spaces || []).some((s) => s.role === "owner" || s.role === "admin"));

// Die Spaces, die CI/CD-Seiten einem Nicht-Global-Admin anbieten duerfen.
export const cicdSpacesVon = (user, spaces) =>
  (user?.role === "admin") ? (spaces || []) : (spaces || []).filter((s) => s.role === "owner" || s.role === "admin");
